/** @jsx jsx */
import { useState, useCallback } from "react"
import { useStaticQuery, graphql } from "gatsby"
import Image from "gatsby-image"
import { Text, Box, Link as ExternalLink, jsx } from "theme-ui"
import BlockContent from "@sanity/block-content-to-react"

const styles = {
  container: {
    padding: ["8px", "8px", "8px", "20px"],
    background: `linear-gradient(#CCC2BA, #EEA33B)`,
  },
  text: {
    margin: ["30px 0 40px", "30px 0 40px", "60px 0 40px", "50px 0 155px"],
    "h3, p": {
      fontSize: ["32px", "32px", "32px", "50px"],
      fontFamily: "body",
      padding: "0px",
      margin: "1em 0",
    },
    a: {
      color: "inherit",
      textDecoration: "underline",
      ":hover": { color: "inherit", opacity: 0.65 },
    },
  },
  image: {
    position: "absolute",
    left: "0px",
    top: "0px",
    width: "100%",
    height: "100%",
  },
  clickArea: {
    position: "absolute",
    width: "50%",
    height: "100%",
  },
}

const About = () => {
  const data = useStaticQuery(graphql`
    query AboutQuery {
      sanityAboutUs(_id: { eq: "aboutUs" }) {
        _rawContent
      }
      sanitySlideshowGallery(_id: { eq: "slideshowGallery" }) {
        images {
          asset {
            localFile {
              childImageSharp {
                fluid(maxWidth: 2000) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
  `)

  const [index, setIndex] = useState(0)
  const { sanityAboutUs, sanitySlideshowGallery } = data
  const galleryImages = sanitySlideshowGallery.images.map(
    image => image.asset.localFile.childImageSharp.fluid
  )

  const BlockRenderer = props => {
    if (props.node.style === "h2") {
      return (
        <Text as="h3" color="secondaryText" variant="body">
          {props.children}
        </Text>
      )
    }
    if (props.node.style === "normal") {
      return (
        <Text as="p" color="secondaryText" variant="body">
          {props.children}
        </Text>
      )
    }
    return BlockContent.defaultSerializers.types.block(props)
  }

  const handlePrevious = useCallback(
    () =>
      setIndex(state => {
        return state - 1 < 0 ? galleryImages.length - 1 : state - 1
      }),
    [galleryImages.length]
  )

  const handleNext = useCallback(
    () =>
      setIndex(state => {
        return state + 1 > galleryImages.length - 1 ? 0 : state + 1
      }),
    [galleryImages.length]
  )

  return (
    <Box id="meet-marco" as="section" sx={styles.container}>
      <Box sx={styles.text}>
        <BlockContent
          blocks={sanityAboutUs._rawContent}
          serializers={{
            marks: {
              link: ({ mark, children }) => {
                const { blank, href } = mark
                return blank ? (
                  <ExternalLink target="_blank" rel="noopener" href={href}>
                    {children}
                  </ExternalLink>
                ) : (
                  <ExternalLink sx={styles.link} href={href}>
                    {children}
                  </ExternalLink>
                )
              },
            },
            types: {
              block: BlockRenderer,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100vh",
          maxHeight: ["60vh", null, null, "100vh"],
        }}
      >
        {galleryImages.map((image, idx) => {
          return (
            <div sx={styles.image} key={idx}>
              <Image
                style={{
                  opacity: idx === index ? 1 : 0,
                  width: "100%",
                  height: "100%",
                }}
                imgStyle={{
                  backgroundColor: "#DD8E33",
                  objectFit: "cover",
                  objectPosition: "50% 50%",
                }}
                fluid={image}
                alt={image.alt || `Gallery Image ${idx + 1}`}
              />
            </div>
          )
        })}
        <Box
          onClick={handlePrevious}
          sx={{
            ...styles.clickArea,
            left: "0px",
            cursor: `url(/images/prev.png) 0 40, auto`,
          }}
        />
        <Box
          onClick={handleNext}
          sx={{
            ...styles.clickArea,
            right: "0px",
            cursor: `url(/images/next.png) 128 40, auto`,
          }}
        />
      </Box>
    </Box>
  )
}

export default About
